<script lang="ts" setup>
import { ref, inject, watch, onMounted, onBeforeUnmount } from 'vue';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { PerspectiveEvents } from '@/viewerExtensions/events';
import { waitFor } from '@immersive-tech/viewer-engine';
import CollapseFieldset from '@/components/CollapseFieldset.vue';
import ToggleBox from '@/components/ToggleBox.vue';
import ZoomRestriction from '@/components/Perspectives/ZoomRestriction.vue';
import AngleRestriction from '@/components/Perspectives/AngleRestriction.vue';
import CameraCoordinate from '@/components/Perspectives/CameraCoordinate.vue';
import MobileOffset from '@/components/Perspectives/MobileOffset.vue';

import type { Ref } from 'vue';
import type { Viewer, Nullable } from '@immersive-tech/viewer-engine';
import type { IManifestView, IManifest, ManifestViewZoomRestriction } from '@/types/IManifest';
import type { IExtensionCameraPositionChangeEvent } from '@/types/events/IExtensionCameraPositionChangeEvent';
import type { IExtensionCameraTargetChangeEvent } from '@/types/events/IExtensionCameraTargetChangeEvent';

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const manifest = inject('manifest') as Ref<IManifest>;
const cameraPosition = ref(Vector3.Zero());
const cameraTarget = ref(Vector3.Zero());
const eventListenersAdded = ref(false);
const angleRestriction = ref<number | ''>('');
const mobileOffset = ref<number | ''>(0.5);
const perspectiveName = ref('');
const zoomRestriction = ref<ManifestViewZoomRestriction>([0, 10]);
const perspectiveExistsErrorShow = ref(false);
const previewRestrictions = inject('previewRestrictions') as Ref<boolean>;
const skipCameraWatchers = ref(false);

const cameraPositionWatcher = (event: CustomEvent<IExtensionCameraPositionChangeEvent>) => {
  if (previewRestrictions.value === true) return;
  if (skipCameraWatchers.value === true) return;

  cameraPosition.value = event.detail.cameraPosition;
};

const cameraTargetWatcher = (event: CustomEvent<IExtensionCameraTargetChangeEvent>) => {
  if (previewRestrictions.value === true) return;
  if (skipCameraWatchers.value === true) return;

  cameraTarget.value = event.detail.cameraTarget;
};

const resetViewHandler = () => {
  previewRestrictions.value = false;
};

const addEventListeners = () => {
  if (eventListenersAdded.value === true) return;

  viewer.value?.addEventListener(PerspectiveEvents.CAMERA_POSITION_CHANGE, cameraPositionWatcher as EventListener);
  viewer.value?.addEventListener(PerspectiveEvents.CAMERA_TARGET_CHANGE, cameraTargetWatcher as EventListener);
  viewer.value?.addEventListener(PerspectiveEvents.RESET_VIEW, resetViewHandler);
};

const getStartingCameraValues = () => {
  if (!viewer.value) return;
  if (!viewer.value.camera) return;

  cameraTarget.value = viewer.value.camera.target.clone();
  cameraPosition.value = viewer.value.camera.position.clone();
};

const mountHandler = () => {
  getStartingCameraValues();
  addEventListeners();
};

const unmountHandler = () => {
  previewRestrictions.value = false;

  viewer.value?.removeEventListener(PerspectiveEvents.CAMERA_POSITION_CHANGE, cameraPositionWatcher as EventListener);
  viewer.value?.removeEventListener(PerspectiveEvents.CAMERA_TARGET_CHANGE, cameraTargetWatcher as EventListener);
  viewer.value?.removeEventListener(PerspectiveEvents.RESET_VIEW, resetViewHandler);
};

const createPerspective = (newPerspectiveName: string) => {
  if (newPerspectiveName === '') return;

  const { x: targetX, y: targetY, z: targetZ } = cameraTarget.value as Vector3;
  const { x: positionX, y: positionY, z: positionZ } = cameraPosition.value as Vector3;

  const newPerspective = {
    target: { x: targetX, y: targetY, z: targetZ },
    position: { x: positionX, y: positionY, z: positionZ },
    angle: angleRestriction.value === '' ? null : angleRestriction.value,
    zoomRestriction: [0, 10],
    mobileOffset: mobileOffset.value === '' ? null : mobileOffset.value,
  } as IManifestView;

  if (newPerspectiveName in manifest.value.views) {
    perspectiveExistsErrorShow.value = true;

    return;
  }

  manifest.value.views[newPerspectiveName] = newPerspective;

  angleRestriction.value = 0;
  perspectiveName.value = '';
  zoomRestriction.value = [0, 10];
};

const perspectiveExistsErrorShowWatcher = async (newValue: boolean) => {
  if (newValue === false) return;

  await waitFor(1500);

  perspectiveExistsErrorShow.value = false;
};

const viewerWatcher = (newValue: Nullable<Viewer>) => {
  if (!newValue) return;

  addEventListeners();
};

const previewRestrictionsWatcher = (newValue: boolean) => {
  if (newValue === true) {
    skipCameraWatchers.value = true;
  } else {
    viewer.value?.addEventListener(PerspectiveEvents.ZOOM_RESTRICTION_SET, async () => {
      await viewer.value?.interpolateCamera(cameraPosition.value as Vector3, cameraTarget.value as Vector3, 0);

      skipCameraWatchers.value = false;
    }, { once: true });
  }
};

const updateAngleRestriction = (newValue: Nullable<number>) => {
  angleRestriction.value = newValue ?? '';
};

const updateZoomRestriction = (newValue: [number, number]) => {
  zoomRestriction.value = newValue;
};

const updateCameraTarget = (newCoordinate: Vector3) => {
  if (!viewer.value) return;

  if (viewer.value.camera) {
    viewer.value.camera.target = newCoordinate;
  }

  viewer.value.renderNextFrame += 1;
};

const updateCameraPosition = (newCoordinate: Vector3) => {
  if (!viewer.value) return;

  if (viewer.value.camera) {
    viewer.value.camera.position = newCoordinate;
  }

  viewer.value.renderNextFrame += 1;
};

const updateMobileOffset = (newValue: Nullable<number>) => {
  mobileOffset.value = newValue ?? '';
};

watch(viewer, viewerWatcher);
watch(perspectiveExistsErrorShow, perspectiveExistsErrorShowWatcher);
watch(previewRestrictions, previewRestrictionsWatcher);
onMounted(mountHandler);
onBeforeUnmount(unmountHandler);
</script>

<template>
  <CollapseFieldset>
    <template #legend>
      Create Perspective
    </template>

    <p class="text-xs pb-1 my-0.5">
      Create a new perspective from the current camera position and target
    </p>

    <div v-if="perspectiveExistsErrorShow === true" class="bg-red-100 p-2 mb-1">
      <p class="text-xs">
        Perspective "<span class="font-bold">{{ perspectiveName }}</span>" already exists, choose a different name
      </p>
    </div>

    <div class="flex flex-col mb-1">
      <label for="perspective-name" class="text-xs">Perspective Name</label>
      <input id="perspective-name" v-model="perspectiveName" class="border text-xs p-1" type="text">
    </div>

    <div class="flex flex-col mb-1">
      <label for="perspective-angle" class="text-xs">Perspective Angle Restriction</label>
      <AngleRestriction :angle-restriction="angleRestriction ?? ''" @change="updateAngleRestriction" />
    </div>

    <div class="flex flex-col grow mb-1">
      <label class="text-xs">Perspective Zoom Restriction</label>

      <ZoomRestriction :zoom-restriction="zoomRestriction" @change="updateZoomRestriction" />
    </div>

    <div class="flex flex-col mb-1">
      <label for="perspective-offset" class="text-xs">Mobile Offset</label>
      <MobileOffset :mobile-offset="mobileOffset" @change="updateMobileOffset" />
    </div>

    <div class="flex flex-col mb-1">
      <ToggleBox v-model="previewRestrictions" label="Preview Restrictions" />
    </div>

    <div class="flex flex-col mb-1">
      <label class="text-xs">Camera Position</label>
      <CameraCoordinate :coordinate="cameraPosition" :editable="true" @change="updateCameraPosition" />
    </div>

    <div class="flex flex-col mb-1">
      <label class="text-xs">Camera Target</label>
      <CameraCoordinate :coordinate="cameraTarget" :editable="true" @change="updateCameraTarget" />
    </div>

    <div class="flex flex-col px-1 my-1">
      <button type="button" class="text-xs p-1 bg-lime-600 text-white" @click="() => createPerspective(perspectiveName) ">
        Create
      </button>
    </div>
  </CollapseFieldset>
</template>
